'use client';

import { Link } from '@packages/modules/src/Link/Link';
import { useTrackMarketing } from '@packages/shared/src/hooks/useTrackCampaign/useTrackCampaign';
import { type Ref, useRef } from 'react';
import type { CmsImageType } from '../../components/CmsImage/types';
import { useAssignLinkTargetBasedOnBotStatus } from '../../hooks/useAssignLinkTargetBasedOnBotStatus';
import { buildLinkWithArguments } from '../../utils/buildLinkWithArguments';
import { BannerItemImage } from './BannerItemImage.client';

export type BannerItemType = {
  data: CmsImageType;
};

/**
 * Component that displays a banner image.
 * If a link is provided in the data, the image acts as a clickable link.
 *
 * @component
 * @param {Object} props - Properties passed to the component.
 * @param {CmsImageType} props.data - Data object containing banner details.
 * @returns {JSX.Element} A rendered banner item.
 *
 * @example
 * // Usage example:
 * <BannerItem data={someCmsImageData} />
 */
export const BannerItem = ({ data }: BannerItemType) => {
  const {
    field_link: link,
    field_link_target: linkTarget,
    field_tracking: linkTracking,
    field_link_anchor: linkAnchor,
    field_link_highlight: linkHighlight,
    field_link_searchfilter: linkSearchFilter,
    field_bonus_number: bonusNumber,
    behavior_paragraph_marketing_display_type: marketingType,
    behavior_paragraph_campaign_type: campaignType,
    behavior_paragraph_campaignid: campaignId,
    behavior_paragraph_category: category,
    behavior_paragraph_detail: detail,
    behavior_paragraph_name: name,
  } = data;

  const tmpLink = buildLinkWithArguments(link, {
    field_tracking: linkTracking || undefined,
    field_link_anchor: linkAnchor || undefined,
    field_link_highlight: linkHighlight || undefined,
    field_link_searchfilter: linkSearchFilter || undefined,
    field_bonus_number: bonusNumber || undefined,
  });

  const assignLinkTarget = useAssignLinkTargetBasedOnBotStatus();
  const finalLink = assignLinkTarget(tmpLink);

  const ref = useRef<HTMLDivElement | HTMLAnchorElement>(null);

  const isAd = marketingType === 'ad';
  const isCampaign = !isAd; // for backwards compatibility, every marketing that's not an ad is a campaign

  const { trackClick } = useTrackMarketing(
    {
      linkReady: finalLink,
      ...(campaignType && {
        ...(isAd && {
          glycerinAdId: campaignId ?? undefined, // yes, the campaignid from drupal is used as adId
          glycerinAdType: campaignType ?? undefined, // yes, the campaigntype from drupal is used as adType
        }),
        ...(isCampaign && {
          glycerinCampaignId: campaignId ?? undefined,
          glycerinCampaignType: campaignType ?? undefined,
        }),

        glycerinCategory: category ?? undefined,
        glycerinDetail: detail ?? undefined,
        glycerinName: name ?? undefined,
      }),
    },
    ref,
  );

  const classes = 'mb-2 block [&_img]:block [&_img]:h-auto [&_img]:max-w-full';

  if (link && link !== '') {
    return (
      <Link
        ref={ref as Ref<HTMLAnchorElement>}
        className={classes}
        href={finalLink}
        openInNewTab={!!(linkTarget && linkTarget === '_blank')}
        onClick={(event) => {
          event.preventDefault();

          // Prevent banner click if the target is within the tooltip
          const targetElement = event.target as HTMLElement;
          if (targetElement.closest('.tooltip-cms button')) {
            return;
          }

          window.location.assign(finalLink);

          trackClick();
        }}
      >
        <BannerItemImage data={data} />
      </Link>
    );
  }

  return (
    <div ref={ref as Ref<HTMLDivElement>} className={classes}>
      <BannerItemImage data={data} />
    </div>
  );
};
